import React, { useState } from "react";
import { BookOpen, Search } from "lucide-react";
import { useI18n } from "../lib/i18n";

const SECTIONS = [
  {
    key: "workspace",
    pt: { title: "Meu espaço", desc: "Recursos para seu dia a dia" },
    en: { title: "Workspace", desc: "Your day-to-day tools" },
    items: [
      { key: "dashboard", pt: ["Início", "Saldos PTO/Férias, smart alerts, atalhos rápidos e os valores Sagewood: Quality, Care e Commitment, com badge de 25 anos."], en: ["Home", "PTO/Vacation balances, smart alerts, quick actions and the Sagewood values: Quality, Care and Commitment, with the 25-years badge."] },
      { key: "planner", pt: ["Planner", "Agenda mensal agregando PTO, treinos, avaliações, follow-up 90 dias, terminations e lembretes pessoais."], en: ["Planner", "Monthly agenda aggregating PTO, trainings, evaluations, 90-day follow-ups, terminations and personal reminders."] },
      { key: "pto", pt: ["PTO & Férias", "Solicitar PTO/Vacation, conversar com aprovador via thread de comentários, ver saldos em tempo real."], en: ["PTO & Vacation", "Request PTO/Vacation, chat with approver via comments thread, see balances in real time."] },
      { key: "travel", pt: ["Despesas do integrante", "Despesas pessoais (refeições, hospedagem, transporte, milhagem) com validação automática contra a política Sagewood — limites por cargo."], en: ["Team member Expenses", "Personal expenses (meals, lodging, transport, mileage) auto-validated against Sagewood policy — role-based tier limits."] },
      { key: "reimbursements", pt: ["Reembolsos de projeto", "Custos de projeto com material/serviço + cost code. Após aprovação, clique em Submeti no RedTeam → o portal dispara automaticamente o QuickBooks (VendorCredit + Bill) e acompanha o pagamento."], en: ["Project reimbursements", "Project costs with material/service + cost code. After approval click Submit on RedTeam → the portal auto-triggers QuickBooks (VendorCredit + Bill) and tracks payment."] },
      { key: "trainings", pt: ["Treinamentos", "Catálogo + meus treinos + team compliance (managers). Auto-assign no onboarding por role e eligibility rules."], en: ["Trainings", "Catalog + my trainings + team compliance (managers). Auto-assign at onboarding by role and eligibility rules."] },
    ],
  },
  {
    key: "people",
    pt: { title: "Pessoas", desc: "Conhecer e gerir o time" },
    en: { title: "People", desc: "Know and manage your team" },
    items: [
      { key: "directory", pt: ["Diretório", "Perfis clicáveis, aniversariantes do mês, ex-integrantes arquivados separadamente."], en: ["Directory", "Clickable profiles, month birthdays, former team members archived separately."] },
      { key: "departments", pt: ["Hub de departamentos", "Diagramas dos departamentos + quiz de onboarding por área."], en: ["Department Hub", "Department diagrams + per-area onboarding quiz."] },
      { key: "org-chart", pt: ["Organograma", "Árvore hierárquica navegável + export PDF + clique no nó para abrir perfil."], en: ["Org Chart", "Navigable hierarchical tree + PDF export + click node to open profile."] },
      { key: "performance", pt: ["Desempenho", "Ciclos de avaliação, auto-avaliação x manager, PIPs (planos de melhoria)."], en: ["Performance", "Eval cycles, self vs manager evaluations, PIPs (improvement plans)."] },
      { key: "hiring", pt: ["Contratações", "Kanban de vagas + candidatos + auto-criação de onboarding completo ao hire."], en: ["Hiring", "Vacancies + applicants Kanban + auto-onboarding on hire."] },
      { key: "offboarding", pt: ["Desligamentos", "Checklist DocuSign + FrankCrum + equipment return. Arquiva como Former team member."], en: ["Offboarding", "DocuSign + FrankCrum + equipment return checklist. Archives as Former team member."] },
      { key: "salary-matrix", pt: ["Matriz salarial (confidencial)", "Matriz Tracks × Levels editável, histórico salarial, approval chain por movimentação."], en: ["Salary Matrix (confidential)", "Editable Tracks × Levels grid, salary history, per-movement approval chain."] },
    ],
  },
  {
    key: "operations",
    pt: { title: "Operações", desc: "Aprovar, consultar, pagar" },
    en: { title: "Operations", desc: "Approve, look up, pay" },
    items: [
      { key: "approvals", pt: ["Aprovações pendentes", "Caixa única para PTO, reembolsos, expenses, loans e salary moves."], en: ["Pending approvals", "Single inbox for PTO, reimbursements, expenses, loans and salary moves."] },
      { key: "loans", pt: ["Empréstimos", "Confidencial — só RH cria. Parcelamento automático + approval chain."], en: ["Loans", "Confidential — HR-only creation. Auto-installments + approval chain."] },
      { key: "documents", pt: ["Documentos", "Biblioteca de políticas, contratos modelo, formulários."], en: ["Documents", "Library of policies, model contracts, forms."] },
      { key: "knowledge", pt: ["Base de conhecimento", "Q&A verificadas + Sage Copilot (Claude Sonnet 4.5) com citação de fontes."], en: ["Knowledge Base", "Verified Q&As + Sage Copilot (Claude Sonnet 4.5) with cited sources."] },
    ],
  },
  {
    key: "system",
    pt: { title: "Sistema", desc: "Personalização e perfil" },
    en: { title: "System", desc: "Customisation and profile" },
    items: [
      { key: "copilot", pt: ["Sage Copilot", "Assistente AI bilingue para perguntas sobre políticas, procedimentos, saldos e mais."], en: ["Sage Copilot", "Bilingual AI assistant for questions about policies, procedures, balances and more."] },
      { key: "profile", pt: ["Meu perfil", "Atualizar dados pessoais, foto, contato de emergência. Acesso aos seus documentos."], en: ["My profile", "Update personal data, photo, emergency contact. Access your documents."] },
      { key: "admin", pt: ["Admin & RH", "Cost codes, departamentos, approval workflows, Travel policy, regras de notificação, demo & manual."], en: ["Admin & HR", "Cost codes, departments, approval workflows, Travel policy, notification rules, demo & manual."] },
      { key: "manual", pt: ["Manual & Tours", "Esta página + Tours guiados (botão no topo direito de cada tela) para um passo-a-passo interativo."], en: ["Manual & Tours", "This page + Guided tours (button on the top-right of each screen) for an interactive walk-through."] },
    ],
  },
];

export default function Manual() {
  const { lang } = useI18n();
  const [q, setQ] = useState("");

  const filter = (it) => {
    if (!q) return true;
    const hay = `${it[lang][0]} ${it[lang][1]}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><BookOpen size={22} /></div>
        <div>
          <h1 className="sw-heading text-[28px]">{lang === "pt" ? "Manual do usuário" : "User manual"}</h1>
          <p className="text-[14px] text-[#747474]">{lang === "pt" ? "Recursos e funções disponíveis no portal Sagewood" : "Features and functions available in the Sagewood portal"}</p>
        </div>
      </div>

      <div className="sw-card !p-3 flex items-center gap-2 max-w-[520px]">
        <Search size={14} className="text-[#94a3b8]" />
        <input
          data-testid="manual-search"
          placeholder={lang === "pt" ? "Buscar feature ou termo..." : "Search feature or term..."}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 outline-none text-[13.5px] text-[#1e293b] bg-transparent"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {SECTIONS.map((s) => {
          const visibleItems = s.items.filter(filter);
          if (q && visibleItems.length === 0) return null;
          return (
            <div key={s.key} data-testid={`manual-section-${s.key}`} className="sw-card">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="sw-heading text-[18px]">{s[lang].title}</h2>
                <span className="text-[10px] uppercase tracking-[0.12em] text-[#94a3b8]">{visibleItems.length} {lang === "pt" ? "itens" : "items"}</span>
              </div>
              <p className="text-[12px] text-[#747474] mb-4">{s[lang].desc}</p>
              <div className="flex flex-col divide-y divide-[#f1f5f9]">
                {visibleItems.map((it) => (
                  <div key={it.key} className="py-3">
                    <div className="text-[13.5px] font-semibold text-[#1e293b]">{it[lang][0]}</div>
                    <div className="text-[12.5px] text-[#475569] mt-1 leading-relaxed">{it[lang][1]}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="sw-card bg-gradient-to-br from-[#1e293b] via-[#475569] to-[#334155] text-white !p-6">
        <div className="text-[10px] uppercase tracking-[0.16em] text-white/70">{lang === "pt" ? "Dica" : "Tip"}</div>
        <div className="text-[15px] font-medium mt-1">
          {lang === "pt"
            ? "Em qualquer página, clique no botão Tour guiado (canto superior direito) para um passo-a-passo daquela tela."
            : "On any page, click the Guided tour button (top-right corner) for a step-by-step of that screen."}
        </div>
      </div>
    </div>
  );
}
